import type { ReactNode } from 'react'

export type WidgetOptInCheckboxProps = {
	optedIn: boolean
	onOptIn: () => void
	onOptOut: () => void
	children: ReactNode
}

/**
 * Wraps a widget input with a checkbox that enables/disables the input.
 *
 * When not opted in, children are dimmed but remain interactive. Capture-phase
 * handlers on pointer and keyboard events call `onOptIn` before the event
 * reaches children — so the opt-in and the child's value change are both
 * dispatched in the same tick. This works without stale-closure issues as
 * long as the `onOptIn` callback writes a partial/functional update to the
 * store rather than spreading a captured state snapshot.
 */
export function WidgetOptInCheckbox({ optedIn, onOptIn, onOptOut, children }: WidgetOptInCheckboxProps) {
	return (
		<div className="flex items-center gap-1.5">
			<input
				type="checkbox"
				checked={optedIn}
				onChange={(e) => {
					if (e.target.checked) {
						onOptIn()
					} else {
						onOptOut()
					}
				}}
				className="size-3.5 cursor-pointer accent-blue-500"
			/>
			<div
				className={`flex-1 ${optedIn ? '' : 'opacity-40'}`}
				onPointerDownCapture={() => {
					if (!optedIn) { onOptIn() }
				}}
				onKeyDownCapture={(e) => {
					if (optedIn) { return }
					const ignore = e.key === 'Tab' || e.key === 'Escape'
						|| e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta'
					if (!ignore) { onOptIn() }
				}}
			>
				{children}
			</div>
		</div>
	)
}
